/* Solvel theme: header, cart drawer, product form, gallery. No dependencies. */
(() => {
  const { routes, strings, moneyFormat } = window.solvel || { routes: {}, strings: {} };
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (moneyFormat || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) =>
      key === 'amount_no_decimals' ? withCommas.split('.')[0] : key === 'amount_with_comma_separator' ? amount.replace('.', ',') : withCommas
    );
  };

  /* Header: border once scrolled, mobile menu */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const toggle = header.querySelector('[data-menu-toggle]');
    const nav = document.getElementById('mobile-nav');
    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      nav.hidden = open;
    });
  }

  /* Cart drawer */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.querySelectorAll('[data-drawer-open]').forEach((a) =>
        a.addEventListener('click', (e) => {
          if (document.body.classList.contains('template-cart')) return;
          e.preventDefault();
          this.open();
        })
      );
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-line-change]');
        if (btn) this.change(btn.dataset.lineChange, Number(btn.dataset.quantity), btn);
      });
    }
    open() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('drawer-open');
      this.querySelector('.drawer__head .icon-btn')?.focus();
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      this.lastFocus?.focus?.();
    }
    async refresh(sectionsHtml) {
      const html = sectionsHtml?.['cart-drawer'] ?? (await (await fetch(`${routes.root}?sections=cart-drawer`)).json())['cart-drawer'];
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const target = document.getElementById('shopify-section-cart-drawer');
      const fresh = doc.getElementById('shopify-section-cart-drawer') || doc.body.firstElementChild;
      if (target && fresh) target.innerHTML = fresh.innerHTML;
      const count = doc.querySelector('[data-cart-count]')?.textContent.trim() || '0';
      document.querySelectorAll('[data-cart-count-badge]').forEach((b) => (b.textContent = count === '0' ? '' : count));
    }
    /* Free gifts carry _gift_for / _gift_min: remove the gift if its qualifying item drops below the minimum. */
    async checkGifts(data) {
      const items = data.items || [];
      for (const g of items.filter((i) => i.properties && i.properties._gift_for)) {
        const need = Number(g.properties._gift_min || 1);
        const have = items.filter((i) => String(i.variant_id) === g.properties._gift_for).reduce((n, i) => n + i.quantity, 0);
        if (have < need) {
          const res = await fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: g.key, quantity: 0, sections: ['cart-drawer'] }) });
          return res.json();
        }
      }
      return null;
    }
    async change(key, quantity, btn) {
      btn?.classList.add('is-loading');
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'] })
        });
        let data = await res.json();
        data = (await this.checkGifts(data)) || data;
        if (document.body.classList.contains('template-cart')) return window.location.reload();
        await this.refresh(data.sections);
      } catch (e) {
        btn?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Product form: variant pills, quantity, AJAX add */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.addBtn = this.querySelector('[data-add]');
      this.buyBtn = this.querySelector('[data-buy-now]');
      /* Coming back from checkout with the Back button: don't leave Buy now spinning. */
      window.addEventListener('pageshow', () => this.buyBtn?.classList.remove('is-loading'));
      this.error = this.querySelector('[data-error]');
      this.querySelectorAll('[data-option-index]').forEach((r) => r.addEventListener('change', () => this.onOption()));
      this.querySelectorAll('[data-qty]').forEach((b) =>
        b.addEventListener('click', () => {
          const input = this.querySelector('input[name="quantity"]');
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qty));
        })
      );
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.offerQty = this.querySelector('[data-offer-quantity]');
      this.querySelectorAll('[data-offer-qty]').forEach((r) => r.addEventListener('change', () => { if (this.offerQty) this.offerQty.value = r.dataset.offerQty; }));
      /* Buy 2 ads: a link whose utm_content contains "buy-2" opens on Buy 2. Everyone else stays on Buy 1. */
      if (/(^|-)buy-2(-|$)/.test(new URLSearchParams(location.search).get('utm_content') || '')) this.querySelector('.offer--best [data-offer-qty]')?.click();
    }
    onOption() {
      const picked = [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
      picked.forEach((v, i) => { const l = this.querySelector(`[data-option-label="${i}"]`); if (l) l.textContent = v; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === picked[i]));
      const label = this.addBtn.querySelector('[data-add-label]');
      if (!variant) {
        this.addBtn.disabled = true;
        if (this.buyBtn) this.buyBtn.disabled = true;
        label.textContent = strings.unavailable;
        return;
      }
      this.idInput.value = variant.id;
      this.addBtn.disabled = !variant.available;
      if (this.buyBtn) this.buyBtn.disabled = !variant.available;
      label.textContent = variant.available ? (label.dataset.label || strings.add) : strings.soldOut;
      const section = this.closest('section');
      const priceBox = section?.querySelector('[data-price]');
      if (priceBox) {
        priceBox.querySelector('span').textContent = formatMoney(variant.price);
        const sale = variant.compare_at_price > variant.price;
        const cmp = priceBox.querySelector('[data-compare]');
        const save = priceBox.querySelector('[data-save]');
        if (cmp) { cmp.hidden = !sale; if (sale) cmp.querySelector('[data-compare-amount]').textContent = formatMoney(variant.compare_at_price); }
        if (save) { save.hidden = !sale; if (sale) save.textContent = priceBox.dataset.saveText.replace('__AMOUNT__', formatMoney(variant.compare_at_price - variant.price)); }
      }
      const one = this.querySelector('[data-offer-compare]');
      if (one) { const sale = variant.compare_at_price > variant.price; one.hidden = !sale; if (sale) one.textContent = formatMoney(variant.compare_at_price); }
      const offers = this.querySelector('[data-offers]');
      if (offers) {
        const pct = Number(offers.dataset.pct || 0);
        offers.querySelectorAll('[data-offer-price]').forEach((el) => {
          const q = Number(el.dataset.offerPrice);
          el.textContent = formatMoney(q > 1 ? (variant.price - Math.floor(variant.price * pct / 100)) * q : variant.price);
        });
        const full = offers.querySelector('[data-offer-full]');
        const bq = Number(offers.querySelector('.offer--best [data-offer-qty]')?.dataset.offerQty || 2);
        if (full) full.textContent = formatMoney(variant.price * bq);
      }
      const vImg = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
      const mediaId = variant.featured_media?.id;
      const slide = mediaId && section?.querySelector(`[data-gallery] .gallery__slide[data-media-id="${mediaId}"]`);
      if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      if (section?.closest('.template-product')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
    async onSubmit(e) {
      /* Buy now: same add as Add to cart (offer qty + free gift), then straight to checkout instead of the drawer. */
      const buyNow = !!e.submitter?.hasAttribute('data-buy-now');
      const drawer = document.querySelector('cart-drawer');
      if (!drawer && !buyNow) return;
      e.preventDefault();
      this.error.hidden = true;
      const busy = buyNow ? this.buyBtn : this.addBtn;
      busy.classList.add('is-loading');
      const form = new FormData(this.form);
      const items = [{ id: Number(form.get('id')), quantity: Number(form.get('quantity') || 1) }];
      const gift = Number(this.dataset.giftVariant || 0);
      try {
        const giftMin = Number(this.dataset.giftMin || 1);
        if (gift) {
          const cart = await (await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
          const inCart = cart.items.filter((i) => i.variant_id === items[0].id).reduce((n, i) => n + i.quantity, 0);
          if (inCart + items[0].quantity >= giftMin && !cart.items.some((i) => i.variant_id === gift)) {
            items.push({ id: gift, quantity: 1, properties: { _gift: 'yes', _gift_for: String(items[0].id), _gift_min: String(giftMin) } });
          }
        }
        const res = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['cart-drawer'] })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || strings.error);
        if (buyNow) { window.location.href = routes.checkout || '/checkout'; return; }
        await drawer.refresh(data.sections);
        drawer.open();
      } catch (err) {
        this.error.textContent = err.message || strings.error;
        this.error.hidden = false;
        busy.classList.remove('is-loading');
        return;
      }
      if (!buyNow) busy.classList.remove('is-loading');
    }
  }
  customElements.define('product-form', ProductForm);

  /* Season tabs: click or arrow keys */
  class SeasonTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n); this.tabs[n].focus();
          }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
      });
    }
  }
  customElements.define('season-tabs', SeasonTabs);

  /* Sticky add-to-cart: appears once the main buy button scrolls away */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector('[data-buy-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      /* Show from the first screen whenever the real buy buttons are off screen (above or below), so people who never reach them can still buy. */
      let anchorVisible = true;
      const update = () => this.classList.toggle('is-visible', !anchorVisible && !document.body.classList.contains('drawer-open'));
      new IntersectionObserver(([e]) => { anchorVisible = e.isIntersecting; update(); }).observe(anchor.querySelector('[data-add]') || anchor);
      window.addEventListener('scroll', update, { passive: true });
      new MutationObserver(update).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      /* Price follows the offer picked (Buy 1 / Buy 2). */
      const price = this.querySelector('[data-sticky-price]');
      const syncPrice = () => {
        const picked = anchor.querySelector('[data-offer-qty]:checked');
        const amount = picked && anchor.querySelector(`[data-offer-price="${picked.dataset.offerQty}"]`);
        if (price && amount) price.textContent = amount.textContent;
      };
      anchor.addEventListener('change', () => setTimeout(syncPrice));
      syncPrice();
      this.querySelector('[data-sticky-add]').addEventListener('click', () => anchor.querySelector('[data-add]')?.click());
      this.querySelector('[data-sticky-buy]')?.addEventListener('click', () => anchor.querySelector('[data-buy-now]')?.click());
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* Hero timer chip: a slow, decorative countdown */
  document.querySelectorAll('[data-countdown]').forEach((el) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const start = parseInt(el.dataset.countdown, 10) || 0;
    let left = start;
    const pad = (n) => String(n).padStart(2, '0');
    setInterval(() => {
      left = left > 0 ? left - 1 : start;
      el.textContent = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
    }, 1000);
  });

  /* Gallery: swipe / arrows / dots / thumbnails all move one scroll-snap track */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = [...track.children];
    const dots = [...gallery.querySelectorAll('.gallery__dot')];
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    const current = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => {
      const n = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' });
    };
    const update = () => {
      const i = current();
      dots.forEach((d, k) => d.toggleAttribute('data-active', k === i));
      thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
      const t = thumbs[i];
      if (t) t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
    };
    let raf;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    prev?.addEventListener('click', () => go(current() - 1));
    next?.addEventListener('click', () => go(current() + 1));
    thumbs.forEach((t, k) => t.addEventListener('click', () => go(k)));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
    });
  });
})();
